import React from 'react';
import GameIcon from './GameIcon';

const difficultyColors = {
  easy: 'bg-green-100 text-green-700 border-green-200',
  Beginner: 'bg-green-100 text-green-700 border-green-200',
  medium: 'bg-amber-100 text-amber-700 border-amber-200',
  Intermediate: 'bg-amber-100 text-amber-700 border-amber-200',
  hard: 'bg-red-100 text-red-700 border-red-200',
  Advanced: 'bg-red-100 text-red-700 border-red-200',
};

const occasionIcons = {
  wedding: '💍', beach: '🏖️', winter: '❄️', school: '📚',
  work: '💼', casual: '👕', traditional: '🕌', party: '🎉',
  eid: '🌙', formal: '🎩', sport: '⚽', date: '💐',
};

function StarDisplay({ count = 0, max = 3 }) {
  return (
    <div className="flex gap-0.5">
      {Array.from({ length: max }, (_, i) => (
        <span key={i} className={`text-sm ${i < count ? 'text-amber-400' : 'text-gray-300'}`}>★</span>
      ))}
    </div>
  );
}

export default function MissionSelect({ missions, missionStars, completedOutfits, totalStars, onSelectMission, onBack, gameSettings = {} }) {
  return (
    <div className="min-h-screen bg-[#F5F0E8] animate-fade-up">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-5xl mx-auto px-4 py-5">
          <div className="flex items-center justify-between mb-1">
            <button onClick={onBack} className="text-gray-500 hover:text-gray-800 transition-colors text-sm flex items-center gap-1">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              {gameSettings.mission_exit_button || 'Exit'}
            </button>
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <span className="text-amber-500">★</span>
              <span className="font-semibold">{totalStars || 0}</span>
              <span className="text-gray-400">{gameSettings.mission_total_stars_label || 'total stars'}</span>
            </div>
          </div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 tracking-tight">{gameSettings.mission_select_title || 'Choose Your Mission'}</h1>
          <p className="text-gray-500 text-sm mt-1">{gameSettings.mission_select_subtitle || 'Complete outfit challenges in the souk'}</p>
        </div>
      </div>

      {/* Mission Grid */}
      <div className="max-w-5xl mx-auto px-4 py-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 animate-stagger">
          {missions.filter(m => m.is_active !== false).map(mission => {
            const stars = missionStars?.[mission.id] || 0;
            const isCompleted = completedOutfits?.includes(mission.id);
            const occasion = (typeof mission.occasion === 'string' ? mission.occasion.toLowerCase() : '') || 'casual';
            const icon = mission.icon || gameSettings[`icon_occasion_${occasion}`] || occasionIcons[occasion] || '🎯';
            const diff = mission.difficulty || 'easy';
            const diffClass = difficultyColors[diff] || difficultyColors.easy;

            return (
              <button
                key={mission.id}
                onClick={() => onSelectMission(mission)}
                className="bg-white rounded-2xl shadow-sm border border-gray-100 hover:shadow-md transition-shadow text-left group overflow-hidden"
              >
                {/* Top accent bar */}
                <div className="h-1.5 bg-gradient-to-r from-[#C84B31] to-[#D4A843]" />

                <div className="p-5">
                  {/* Icon/Image + Stars */}
                  <div className="flex items-start justify-between mb-3">
                    {mission.image_url ? (
                      <img src={mission.image_url} alt="" className="w-12 h-12 rounded-xl object-cover" />
                    ) : (
                      <span className="text-3xl"><GameIcon icon={icon} /></span>
                    )}
                    <div className="flex flex-col items-end gap-1">
                      <StarDisplay count={stars} />
                      {isCompleted && (
                        <span className="text-[10px] text-green-600 font-medium">{gameSettings.mission_completed_badge || 'Completed'}</span>
                      )}
                    </div>
                  </div>

                  {/* Title */}
                  <h3 className="font-bold text-gray-900 text-base mb-0.5 group-hover:text-[#C84B31] transition-colors">
                    {mission.name_english || mission.name || 'Untitled Mission'}
                  </h3>
                  {mission.name_darija && (
                    <p className="text-sm text-[#2D6A4F] font-medium mb-2">{mission.name_darija}</p>
                  )}

                  {/* Description */}
                  <p className="text-xs text-gray-500 mb-4 line-clamp-2">
                    {mission.description || gameSettings.mission_default_desc || 'Complete this outfit challenge'}
                  </p>

                  {/* Footer: Difficulty + Cost + Time */}
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={`text-[10px] font-bold px-2.5 py-1 rounded-full border ${diffClass}`}>
                      {diff}
                    </span>
                    <span className="text-[10px] text-gray-500 flex items-center gap-1">
                      <GameIcon icon={gameSettings.icon_token || '🪙'} /> {mission.estimated_cost_tokens || mission.budget_limit || '?'}
                    </span>
                    {mission.time_limit_seconds && (
                      <span className="text-[10px] text-gray-500 flex items-center gap-1">
                        ⏱ {Math.floor(mission.time_limit_seconds / 60)}min
                      </span>
                    )}
                    {mission.required_items && (
                      <span className="text-[10px] text-gray-500">
                        {mission.required_items.length} items
                      </span>
                    )}
                  </div>
                </div>
              </button>
            );
          })}
        </div>

        {missions.filter(m => m.is_active !== false).length === 0 && (
          <div className="text-center py-16">
            <span className="text-5xl mb-4 block"><GameIcon icon={gameSettings.icon_empty_state || '🏪'} /></span>
            <p className="text-gray-500 text-lg">{gameSettings.mission_empty_title || 'No missions available yet'}</p>
            <p className="text-gray-400 text-sm mt-1">{gameSettings.mission_empty_desc || 'Check back soon!'}</p>
          </div>
        )}
      </div>
    </div>
  );
}
